import { useTranslation } from 'react-i18next';
import { Badge } from '@cortex/web-design-system/components/ui/Badge';
import { Card, CardContent } from '@cortex/web-design-system/components/ui/Card';
import { sectionStatus, type BdSectionStatus } from './bd-defs.js';
import { BdStatusBadge } from './BdStatusBadge.js';
import type {
  BdArtifact,
  BdArtifactDef,
  BdProgress,
  BdSavedArtifact,
  BdStep,
} from '../../../lib/api/basic-design.js';

/** 進みの区切りの色。 @graph-connects none */
const SEGMENT_CLASS: Record<BdSectionStatus, string> = {
  saved: 'bg-[var(--color-success)]',
  notApplicable: 'bg-[var(--color-info)]',
  notSaved: 'bg-[var(--color-bg-elevated)]',
};

export interface BdProgressCardProps {
  /** 全節の定義（テンプレートの順） */
  defs: readonly BdArtifactDef[];
  /** 保存済みの節 */
  saved: Partial<Record<BdArtifact, BdSavedArtifact>>;
  /** 完了までの進み（API が数えたもの） */
  progress: BdProgress;
  /** 次の 1 手。読み込み中・失敗時は null */
  step: BdStep | null;
  /** 節を開く */
  onSelect: (artifact: BdArtifact) => void;
}

/**
 * 基本設計の進み具合（全節の状態と、保存した数・必須の数）と、次にやること。
 *
 * 完了かどうか・次にやることは API が決める（MCP の get_next_bd_step と同じ判定）。画面は
 * 節ごとの状態を並べ、押せばその節を開く。必須の印を付けるのは、任意の節は「該当なし」で
 * 済ませてよいと読み手に分かるようにするため。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 基本設計の全節の状態（保存済み・未保存・該当なし）と必須の印、完了までの数、次にやることと Claude に頼むときの手順を出す
 * @graph-connects sectionStatus [calls] 節の状態
 * @graph-connects BdStatusBadge [calls] 節の状態の札
 */
export function BdProgressCard({ defs, saved, progress, step, onSelect }: BdProgressCardProps) {
  const { t } = useTranslation();

  return (
    <Card padding="sm">
      <CardContent className="mt-0 space-y-4">
        <div className="flex flex-wrap items-center gap-3">
          <p className="text-sm font-semibold text-[var(--color-text-primary)]">
            {t('bd.progress.summary', {
              saved: progress.saved,
              total: progress.total,
              requiredSaved: progress.requiredSaved,
              requiredTotal: progress.requiredTotal,
            })}
          </p>
          {progress.done ? (
            <Badge size="sm" variant="success">
              {t('bd.progress.done')}
            </Badge>
          ) : null}
          <div
            role="progressbar"
            aria-label={t('bd.progress.aria')}
            aria-valuemin={0}
            aria-valuemax={progress.total}
            aria-valuenow={progress.saved}
            className="flex h-1.5 min-w-32 flex-1 gap-0.5"
          >
            {defs.map((def) => (
              // 節ごとに 1 区切り。幅を割合で出すとインラインの style が要るので、区切りの色で進みを出す
              <span
                key={def.key}
                className={`h-full flex-1 rounded-full ${SEGMENT_CLASS[sectionStatus(def, saved[def.key])]}`}
              />
            ))}
          </div>
        </div>

        <ul
          aria-label={t('bd.progress.sectionsAria')}
          className="grid grid-cols-1 gap-1 sm:grid-cols-2 lg:grid-cols-3"
        >
          {defs.map((def) => {
            const current = saved[def.key];
            const isNext = step?.artifact === def.key;
            return (
              <li key={def.key}>
                <button
                  type="button"
                  aria-current={isNext ? 'step' : undefined}
                  className={[
                    'flex w-full items-center gap-2 rounded-lg border px-2 py-1.5 text-left text-xs hover:bg-[var(--color-bg-hover)]',
                    isNext ? 'border-[var(--color-accent-primary)]' : 'border-transparent',
                  ].join(' ')}
                  onClick={() => {
                    onSelect(def.key);
                  }}
                >
                  <span className="w-12 shrink-0 tabular-nums text-[var(--color-text-muted)]">
                    {def.section}
                  </span>
                  <span className="min-w-0 flex-1 truncate text-[var(--color-text-primary)]">
                    {def.title}
                    {def.requirement === 'required' ? (
                      <span
                        className="ml-0.5 text-[var(--color-error-text)]"
                        title={t('bd.required')}
                      >
                        *
                      </span>
                    ) : null}
                  </span>
                  <BdStatusBadge
                    status={sectionStatus(def, current)}
                    count={current === undefined ? null : current.rows.length}
                  />
                </button>
              </li>
            );
          })}
        </ul>
        <p className="text-xs text-[var(--color-text-muted)]">{t('bd.progress.requiredNote')}</p>

        <div className="border-t border-[var(--color-border)] pt-3">
          {step === null ? (
            <p className="text-sm text-[var(--color-text-muted)]">{t('bd.nextStep.loading')}</p>
          ) : (
            <>
              <p className="text-xs text-[var(--color-text-muted)]">{t('bd.nextStep.label')}</p>
              <p className="mt-0.5 text-sm font-semibold text-[var(--color-text-primary)]">
                {step.action}
              </p>
              <p className="mt-0.5 text-xs text-[var(--color-text-secondary)]">{step.reason}</p>
              {step.pending.length === 0 ? null : (
                <p className="mt-1 text-xs text-[var(--color-text-muted)]">
                  {t('bd.nextStep.pending', {
                    items: step.pending.join(t('bd.nextStep.pendingSeparator')),
                  })}
                </p>
              )}
              {step.runbook.length > 0 ? (
                <details className="mt-3">
                  <summary className="cursor-pointer text-xs text-[var(--color-accent-primary)]">
                    {t('bd.nextStep.runbook')}
                  </summary>
                  <ol
                    aria-label={t('bd.nextStep.runbook')}
                    className="mt-2 list-decimal space-y-1 rounded-lg bg-[var(--color-bg-elevated)] py-2 pl-8 pr-3 font-mono text-xs text-[var(--color-text-secondary)]"
                  >
                    {step.runbook.map((r) => (
                      <li key={r} className="break-all">
                        {r}
                      </li>
                    ))}
                  </ol>
                </details>
              ) : null}
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
